import { zodResolver } from '@hookform/resolvers/zod';
import { type ReactNode, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ApiError, authClient, sessionRefresher } from '@/shared/api';
import { paths, safeReturnTo } from '@/shared/lib/paths';
import { Form } from '@/shared/ui/form';
import { Button } from '@/shared/ui/primitives/button';
import { authErrorMessage } from '../components/auth-errors';
import { type LoginFormValues, loginFormSchema } from '../components/auth-form-schemas';
import { AuthPageLayout } from '../components/auth-page-layout';
import { FormAlert } from '../components/form-alert';
import { TextField } from '../components/text-field';
import { useSession } from '../hooks/use-session';
import { startSession } from '../session';

/** AUTH-1: tela de login por e-mail e senha. */
export function LoginPage(): ReactNode {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const status = useSession((state) => state.status);
  const returnTo = safeReturnTo(searchParams.get('returnTo'));
  const [failure, setFailure] = useState<string | null>(null);
  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: { email: '', password: '' },
  });

  async function submit(values: LoginFormValues): Promise<void> {
    setFailure(null);
    try {
      startSession(await authClient.login(values));
      await navigate(returnTo, { replace: true });
    } catch (error) {
      if (error instanceof ApiError && error.status === 403 && error.code === undefined) {
        await navigate(paths.forbidden(), { replace: true });
        return;
      }
      form.resetField('password');
      setFailure(authErrorMessage(error, 'Não foi possível entrar. Tente de novo.'));
    }
  }

  return (
    <AuthPageLayout title="Entrar no JAF" description="Use o e-mail e a senha da sua conta.">
      {status === 'origin-not-allowed' && (
        <FormAlert>
          Este endereço não é a origem configurada para o JAF (<code>WEB_ORIGIN</code>). Acesse pelo
          endereço configurado, por exemplo <code>http://localhost:5741</code>; para o navegador,{' '}
          <code>127.0.0.1</code> e <code>localhost</code> são origens diferentes.
        </FormAlert>
      )}
      {status === 'unavailable' && (
        <div role="alert" className="space-y-3 rounded-md border border-warning p-4 text-sm">
          <p>Não foi possível falar com o servidor. Verifique sua conexão.</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void sessionRefresher.refresh();
            }}
          >
            Tentar novamente
          </Button>
        </div>
      )}
      <Form {...form}>
        <form
          noValidate
          className="space-y-4"
          onSubmit={(event) => {
            void form.handleSubmit(submit)(event);
          }}
        >
          {failure !== null && <FormAlert>{failure}</FormAlert>}
          <TextField
            control={form.control}
            name="email"
            label="E-mail"
            type="email"
            autoComplete="username"
          />
          <TextField
            control={form.control}
            name="password"
            label="Senha"
            type="password"
            autoComplete="current-password"
          />
          <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
            Entrar
          </Button>
        </form>
      </Form>
      <Link to={paths.forgotPassword()} className="text-sm underline underline-offset-4">
        Esqueci minha senha
      </Link>
    </AuthPageLayout>
  );
}
